'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatNumber, formatPercentageWithNoCalc } from '@/lib/utils';
import type {
  CompanyProfileStable,
  Quote,
  KeyMetricsTTM,
} from '@/lib/types/tools';

interface CompanyHeaderWithTabsProps {
  profile: CompanyProfileStable;
  quote: Quote | null;
  keyMetrics: KeyMetricsTTM | null;
}

const COMPANY_TABS = [
  { id: 'overview', label: 'Overview', href: '' },
  { id: 'financials', label: 'Financials', href: '/financials' },
  { id: 'dividends', label: 'Dividends & Splits', href: '/dividends' },
  { id: 'earnings-call', label: 'Earnings Call', href: '/earnings-call' },
  { id: 'segments', label: 'Revenue Segments', href: '/segments' },
  { id: 'ratios', label: 'Key Ratios', href: '/ratios' },
  {
    id: 'historical-earnings',
    label: 'Historical Earnings',
    href: '/historical-earnings',
  },
  { id: 'news', label: 'News', href: '/news' },
];

export function CompanyHeaderWithTabs({
  profile,
  quote,
  keyMetrics,
}: CompanyHeaderWithTabsProps) {
  const pathname = usePathname();
  const symbol = profile.symbol;

  const getActiveTab = () => {
    const basePath = `/company/${symbol}`;
    if (pathname === basePath) return 'overview';

    const currentTab = COMPANY_TABS.find(
      (tab) => tab.href && pathname === `${basePath}${tab.href}`,
    );
    return currentTab?.id || 'overview';
  };

  const activeTab = getActiveTab();

  return (
    <div className="space-y-6">
      {/* Company Header */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6">
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="flex items-start gap-4">
              {profile.image && (
                <div className="hidden md:block size-16 rounded-md overflow-hidden bg-card p-1">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={profile.image}
                    alt={`${profile.companyName} logo`}
                    className="size-full object-contain drop-shadow-lg text-center items-center justify-center"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      const parent = target.parentElement;
                      if (parent) {
                        const initials = profile.companyName
                          .split(/\s+/)
                          .map((word) => word[0])
                          .slice(0, 2)
                          .join('')
                          .toUpperCase();

                        parent.innerHTML = '';
                        parent.className =
                          'h-16 w-16 rounded-md  flex items-center justify-center';
                        parent.style.fontSize = '24px';
                        parent.style.fontWeight = 'bold';
                        parent.style.color = 'white';
                        parent.textContent = initials;
                      }
                    }}
                  />
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-3xl font-bold">{profile.companyName}</h1>
                  <span className="text-lg font-normal text-muted-foreground">
                    ({profile.symbol})
                  </span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {profile.exchange} · {profile.industry} · {profile.sector}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">
                ${profile.price !== null ? profile.price.toFixed(2) : '—'}
              </div>
              <div
                className={`text-lg ${
                  (profile.change || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {profile.change !== null
                  ? (profile.change >= 0 ? '+' : '') + profile.change.toFixed(2)
                  : '—'}{' '}
                ({formatPercentageWithNoCalc(profile.changePercentage)})
              </div>
            </div>
          </div>
        </div>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <div className="text-sm font-medium text-muted-foreground">
                Market Cap
              </div>
              <div className="text-lg font-semibold">
                {formatNumber(profile.marketCap)}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">
                Beta
              </div>
              <div className="text-lg font-semibold">
                {profile.beta !== null ? profile.beta.toFixed(2) : '—'}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">
                52W Range
              </div>
              <div className="text-lg font-semibold">
                $
                {quote?.yearLow !== undefined && quote.yearLow !== null
                  ? quote.yearLow.toFixed(2)
                  : '—'}{' '}
                - $
                {quote?.yearHigh !== undefined && quote.yearHigh !== null
                  ? quote.yearHigh.toFixed(2)
                  : '—'}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">
                Volume (Avg)
              </div>
              <div className="text-lg font-semibold">
                {formatNumber(profile.averageVolume)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="border-b flex items-center overflow-x-auto">
        <div className="flex mb-4">
          {COMPANY_TABS.map((tab) => (
            <Link
              key={tab.id}
              href={`/company/${symbol}${tab.href}`}
              className={cn(
                'px-4 py-3 text-sm font-medium transition-colors hover:text-primary border-b-2 border-transparent whitespace-nowrap',
                activeTab === tab.id
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              prefetch={true}
            >
              {tab.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
